/**
 * Builders for agent mode user_context objects.
 *
 * Each builder produces an object with the shape:
 *   { type, displayName, llm: { prompt, arguments } }
 *
 * - type:          'view' | 'composite' | 'metric'
 * - displayName:   Short label shown as a pill/chip in the conversation input
 * - llm.prompt:    Natural language string describing the context for the LLM
 * - llm.arguments: Raw data used to generate the prompt
 */

import { formatTrending } from './agent-mode-metric-context';

/**
 * Returns a human-readable description of the current view.
 * Prefers an explicit viewDescription when provided, otherwise falls back
 * to generating one from viewType/viewName.
 */
function getViewDescription ({ viewDescription, viewType, viewName }) {
    const capitalizedType = viewType.charAt(0).toUpperCase() + viewType.slice(1);

    return viewDescription || `the ${capitalizedType} page for "${viewName}"`;
}

/**
 * Builds a structured block of active filters that affect the displayed data.
 * Each filter is on its own line with a "- Key: value" format.
 * Returns an empty string when no filters are set.
 */
export function formatActiveFilters ({ dateRange, apps, segmentName, segmentId, accountName, accountId } = {}) {
    const lines = [];
    if (dateRange) lines.push(`- Date range: ${dateRange}`);
    if (apps?.length) {
        const appEntries = apps
            .map((app) => (app.name ? `"${app.name}" [id: ${app.id}]` : `[id: ${app.id}]`))
            .join(', ');
        lines.push(`- ${apps.length === 1 ? 'App' : 'Apps'}: ${appEntries}`);
    }
    if (segmentName) lines.push(`- Segment: "${segmentName}"${segmentId ? ` [id: ${segmentId}]` : ''}`);
    if (accountName) lines.push(`- Account: "${accountName}"${accountId ? ` [id: ${accountId}]` : ''}`);

    return lines.length ? `\nFilters:\n${lines.join('\n')}` : '';
}

/**
 * Build user_context for a view (e.g. filter bar sparkle button).
 */
export function buildViewContext ({ viewType, viewId, viewName, viewDescription, filters }) {
    const args = { viewType, viewId, viewName };
    const filterBlock = formatActiveFilters(filters);

    return {
        type: 'view',
        displayName: viewName,
        llm: {
            prompt: `The user is on ${getViewDescription({ viewDescription, viewType, viewName })}.${filterBlock}`,
            arguments: args,
        },
    };
}

/**
 * Build user_context for an individual metric.
 * When composite is true, the prompt is a concise summary line for embedding in a composite context.
 */
export function buildMetricContext ({ viewType, viewId, viewName, viewDescription, appId, metricTitle, metricValue, metricTrending, filters, composite = false }) {
    const args = { viewType, viewId, viewName, appId, metricTitle, metricValue, metricTrending };
    const trending = formatTrending(metricTrending);
    const trendingSuffix = trending ? `, ${trending}` : '';
    const view = getViewDescription({ viewDescription, viewType, viewName });
    const filterBlock = formatActiveFilters(filters);

    const prompt = composite
        ? `- ${metricTitle}: ${metricValue}${trendingSuffix}`
        : `The user is on ${view}.\nThey clicked on the "${metricTitle}" metric, which shows ${metricValue}${trendingSuffix}.${filterBlock}`;

    return {
        type: 'metric',
        displayName: metricTitle,
        llm: {
            prompt,
            arguments: args,
        },
    };
}

/**
 * Build user_context for a composite of multiple contexts (e.g. a metric card).
 * Concatenates a description with the prompts of each child context.
 */
export function buildCompositeContext ({ displayName, description, contexts, filters }) {
    const lines = contexts.map((c) => c.llm.prompt);
    const filterBlock = formatActiveFilters(filters);

    return {
        type: 'composite',
        displayName,
        llm: {
            prompt: `${description}\n${lines.join('\n')}${filterBlock}`,
            arguments: contexts.map((c) => c.llm.arguments),
        },
    };
}
